import { Component, computed, input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

export type ButtonColor = 'purple' | 'blue' | 'green' | 'red' | 'white' | 'yellow' | 'gray' | 'dropdown';
export type ButtonSize = 'xxsmall' | 'xsmall' | 'small' | 'medium';

@Component({
  selector: 'Button',
  imports: [NgTemplateOutlet],
  template: `
    @if (href()) {
      <a
          href="{{ href() }}"
          target="{{ target() }}"
          rel="{{ rel() }}"
          class="{{ styles() }}"
      >
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </a>
    } @else {
      <button type="submit" [disabled]="disabled()" class="{{ styles() }}" [class.grayscale]="disabled()" [class.opacity-40]="disabled()">
        <ng-container *ngTemplateOutlet="content"></ng-container>
      </button>
    }

    <ng-template #content>
      <ng-content></ng-content>
    </ng-template>
  `,
})
export class Button {
  class = input('');
  /**
   * Currently has no effect: every Button without an href renders a <button type="submit">.
   * Before COMBI-4468 the template checked `!submit`, which resolved to the (always truthy)
   * `<ng-template #submit>` reference instead of this input. Forms rely on that behaviour.
   */
  submit = input(false);
  disabled = input(false);
  target = input('_self');
  color = input<ButtonColor>('purple');
  href = input('');
  rel = input('opener');
  outline = input(false);
  shadow = input(true);
  size = input<ButtonSize>('medium');

  readonly styles = computed(() => {
    const color = this.color();
    const disabled = this.disabled();
    const size = this.size();

    const styles: string[] = [
      'my-button flex items-center gap-2 rounded-full font-mark-bold font-medium text-center transition-colors duration-200',
      this.class(),
    ];

    // only allow wrapping on "dropdown" (and "green" (selected)) buttons
    switch (color) {
      case 'dropdown':
      case 'green':
        break;
      default:
        styles.push(`whitespace-nowrap`);
        break;
    }

    // Define button style based on outline and color props
    if (this.outline()) {
      styles.push(size === 'xsmall' || size === 'xxsmall' ? 'border' : 'border-2');

      switch (color) {
        case 'blue':
          styles.push(`border-blue-500 text-blue-500`);
          if (!disabled) {
            styles.push(`hover:border-blue-600 hover:bg-blue-600 hover:text-white`);
          }
          break;
        case 'green':
          styles.push(`border-green-500 text-green-500`);
          if (!disabled) {
            styles.push(`hover:border-green-600 hover:bg-green-600 hover:text-white`);
          }
          break;
        case 'red':
          styles.push(`border-red-500 text-red-500`);
          if (!disabled) {
            styles.push(`hover:border-red-600 hover:bg-red-600 hover:text-white`);
          }
          break;
        case 'white':
          styles.push(`border-white text-white`);
          if (!disabled) {
            styles.push(`hover:bg-white hover:text-purple-600`);
          }
          break;
        case 'yellow':
          styles.push(`border-yellow-500 text-yellow-500`);
          if (!disabled) {
            styles.push(`hover:border-yellow-600 hover:bg-yellow-600 hover:text-white`);
          }
          break;
        case 'gray':
          styles.push(`border-gray-400 text-gray-500`);
          if (!disabled) {
            styles.push(`hover:border-gray-800 hover:bg-gray-800 hover:text-white`);
          }
          break;
        default:
          styles.push(`
            border-purple-500 text-purple-500
            hover:border-purple-600 hover:bg-purple-600 hover:text-white
          `);
          break;
      }
    } else {
      if (this.shadow()) {
        styles.push('shadow-lg');
      }

      switch (color) {
        case 'blue':
          styles.push(`
            text-white border-2
            bg-blue-500 border-blue-500 shadow-blue-500/50
            hover:bg-blue-600 hover:border-blue-600
          `);
          break;
        case 'green':
          styles.push(`
            text-white border-2
            bg-green-500 border-green-500 shadow-green-500/50
            hover:bg-green-600 hover:border-green-600
          `);
          break;
        case 'red':
          styles.push(`
            text-white border-2
            bg-red-500 border-red-500 shadow-red-500/50
            hover:bg-red-600 hover:border-red-600 hover:text-white
          `);
          break;
        case 'white':
          styles.push(`
            text-white border-2
            bg-white border-white text-purple-600 shadow-purple-500/50
            hover:bg-purple-500 hover:border-purple-500 hover:text-white
          `);
          break;
        case 'yellow':
          styles.push(`
            text-white border-2
            bg-yellow-500 border-yellow-500 shadow-yellow-500/50
            hover:bg-yellow-600 hover:border-yellow-600
          `);
          break;
        case 'dropdown':
          styles.push(`
            bg-dropdown dark:bg-dropdown-dark shadow-dropdown text-gray-800 dark:text-white
          `);
          break;
        case 'gray':
          styles.push(`
            text-white border-2
            bg-gray-500 border-gray-500 shadow-gray-500/50
            hover:bg-gray-600 hover:border-gray-600
          `);
          break;
        default:
          styles.push(`
            text-white border-2
            bg-purple-500 border-purple-500 shadow-purple-500/40
            hover:bg-purple-600 hover:border-purple-600
          `);
          break;
      }
    }

    // Define button size based on size prop
    switch (size) {
      case 'xxsmall':
        styles.push('px-3 py-0.5 text-xxs rounded-xl');
        break;
      case 'xsmall':
        styles.push('px-3 py-1.5 text-sm rounded-xl');
        break;
      case 'small':
        styles.push('px-6 py-3 text-sm');
        break;
      case 'medium':
      default:
        styles.push('px-6 py-3 text-sm md:px-8 md:py-4 md:text-sm');
        break;
    }

    return styles.join(' ');
  });
}
